import printIcon from "@assets/images/print-inquiry.svg";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import {
  ListOrderConfirmationSapQuery,
  usePrintPdfOrderConfirmMutation
} from "@saleor/graphql";
import { renderCollection } from "@saleor/misc";
import useStyles from "@saleor/OrderConfirm/components/OrderConfirmListTable/styles";
import { OrderReportLanguageEnum } from "@saleor/OrderConfirm/const";
import { OrderConfirmListSortField } from "@saleor/OrderConfirm/urls";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import React, { useRef } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

export interface IOrderConfirmListTableProps
  extends ListProps,
    SortPage<OrderConfirmListSortField> {
  data: ListOrderConfirmationSapQuery["listOrderConfirmationSap"];
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  group?: number | string;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
}

const OrderConfirmListTable: React.FC<IOrderConfirmListTableProps> = props => {
  const {
    data,
    disabled,
    settings,
    pageInfo,
    totalCount,
    latestPageItemNumber,
    sort,
    onSort,
    onPreviousPage,
    onNextPage,
    onUpdateListSettings,
    onNextLastPage,
    onPreviousFirstPage
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [printOrderConfirm] = usePrintPdfOrderConfirmMutation({
    onCompleted: data => {
      const link = document.getElementById("download") as HTMLAnchorElement;
      link.href = `data:application/octet-stream;base64,${data.printPdfOrderConfirmation?.exportedFileBase64}`;
      link.target = "_self";
      link.download = data.printPdfOrderConfirmation?.fileName;
      link.click();
    }
  });

  const handlePrintOrderConfirm = item => {
    printOrderConfirm({
      variables: {
        reportLanguage: OrderReportLanguageEnum.TH,
        orders: {
          ...item,
          createdDate: item.orderDate,
          paymentTermDesc: item.paymentMethodName,
          itemNo: ["10"],
          shipTo: item.shipToCode,
          saleOrg: item.orderLines[0].saleOrg,
          __typename: undefined,
          indexData: undefined,
          indexNumber: undefined,
          orderDate: undefined,
          paymentMethodName: undefined,
          salesOrgName: undefined,
          soldToName: undefined,
          shipToCode: undefined,
          orderLines: item.orderLines.map(line => ({
            ...line,
            orderQty: String(line.orderQty),
            __typename: undefined,
            saleOrg: undefined,
            soldTo: line.soldToCode,
            confirmQty: String(line.confirmQuantity),
            nonConfirmQty: String(line.nonConfirmQuantity),
            requestDate: undefined,
            confirmQuantity: undefined,
            nonConfirmQuantity: undefined,
            soldToCode: undefined,
            soldToName: undefined,
            remarkOrder: undefined,
            confirmDate: undefined,
            iplanConfirmDate: line.confirmDate,
            firstDeliveryDate: line.requestDate,
            remark: line.remarkOrder
          }))
        }
      }
    });
  };

  const renderStatus = status => {
    if (status === "พร้อมส่ง") {
      return (
        <MyTypography variant={"caption"} className={classes.statusReady}>
          พร้อมส่ง
        </MyTypography>
      );
    }
    if (status === "รอผลิต") {
      return (
        <MyTypography variant={"caption"} className={classes.statusInProgress}>
          รอผลิต
        </MyTypography>
      );
    }
    if (status === "ยกเลิก") {
      return (
        <MyTypography variant={"caption"} className={classes.statusCancel}>
          ยกเลิก
        </MyTypography>
      );
    }
    return "";
  };

  return (
    <>
      <MyTypography className={classes.title} variant="h7">
        {`รายการที่เกี่ยวข้อง (${totalCount || 0})`}
      </MyTypography>
      <div className={classes.root} ref={containerRef}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCellHeader
                  className={classNames(
                    classes.col90,
                    classes.cellFreeze2_Customer
                  )}
                >
                  <Box
                    display={"flex"}
                    alignItems="center"
                    justifyContent={"center"}
                  >
                    <SVG src={printIcon} />
                    <Box mr={1} />
                    <MyTypography variant="subtitle3">
                      {intl.formatMessage({ defaultMessage: "Print" })}
                    </MyTypography>
                  </Box>
                </TableCellHeader>
                <TableCellHeader
                  className={classNames(
                    classes.col150,
                    classes.cellFreeze3_Customer
                  )}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Company Name" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classNames(
                    classes.col280,
                    classes.cellFreeze4_Customer
                  )}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Sold to" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classNames(classes.col120, classes.noBorderRight)}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3" align={"center"}>
                    {intl.formatMessage({ defaultMessage: "Order" })}
                    <br />
                    {intl.formatMessage({ defaultMessage: "Create Date" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  direction={
                    sort.sort === OrderConfirmListSortField.soNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(OrderConfirmListSortField.soNo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "S/O No." })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "PO No." })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "สถานะ" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "ลำดับที่" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col320}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "รายละเอียดสินค้า" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "จำนวนสั่ง" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "พร้อมส่ง" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "รอผลิต" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "หน่วย" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "วันที่ต้องการ สินค้า"
                    })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "วันที่ประมาณ การส่งมอบ"
                    })}
                  </MyTypography>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            {renderCollection(data, item => (
              <TableBody key={item.soNo}>
                {renderCollection(item.orderLines, (line, index) => (
                  <TableRow key={`${item.soNo}_${index}`}>
                    <TableCell
                      className={classNames(
                        classes.borderRight,
                        classes.cellFreeze2_Customer,
                        classes.zIndex1
                      )}
                    >
                      {index === 0 && (
                        <MyTypography variant="subtitle3">
                          <Button
                            onClick={() => handlePrintOrderConfirm(item)}
                            variant={"outlined"}
                            color="secondary"
                            className={classes.btnPrint}
                          >
                            Print
                          </Button>
                          <a id="download" download>
                            &nbsp;
                          </a>
                        </MyTypography>
                      )}
                    </TableCell>
                    <TableCell
                      className={classNames(
                        classes.borderRight,
                        classes.cellFreeze3_Customer,
                        classes.zIndex1
                      )}
                    >
                      <MyTypography variant="body3">{`${line.saleOrg} - ${item.salesOrgName}`}</MyTypography>
                    </TableCell>
                    <TableCell
                      className={classNames(
                        classes.borderRight,
                        classes.cellFreeze4_Customer,
                        classes.zIndex1
                      )}
                    >
                      <MyTypography variant="body3">
                        {`${line.soldToCode} - ${line.soldToName}`}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      {index === 0 && (
                        <MyTypography variant={"body3"}>
                          {item.orderDate}
                        </MyTypography>
                      )}
                    </TableCell>
                    <TableCell>
                      {index === 0 && (
                        <MyTypography
                          variant={"subtitle3"}
                          className={classes.soNO}
                        >
                          {item.soNo}
                        </MyTypography>
                      )}
                    </TableCell>
                    <TableCell>
                      {index === 0 && (
                        <MyTypography variant={"body3"}>
                          {item.poNo}
                        </MyTypography>
                      )}
                    </TableCell>
                    <TableCell align={"center"}>
                      {renderStatus(line?.status)}
                    </TableCell>
                    <TableCell>
                      <MyTypography variant={"body3"}>
                        {line.itemNo}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant={"body3"}>
                        {line.materialDescription}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.orderQty}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.confirmQuantity}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.nonConfirmQuantity}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.saleUnit}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.requestDate}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography
                        variant="body3"
                        className={classes.estimatedDate}
                      >
                        {line.confirmDate}
                      </MyTypography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ))}
          </Table>
        </TableContainer>
        {data.length > 0 && (
          <StickyHorizontalScrollBar container={containerRef} />
        )}
      </div>
      <Box className={classes.paginateFooter}>
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount}
          disabled={disabled}
          isBackFistPage
          isNextLastPage
          selectColor="#007E92"
          rowNumberSet={BIG_ROW_NUMBER_SET}
        />
      </Box>
    </>
  );
};

export default OrderConfirmListTable;
